import type { OrderItem, ProductVariantDetails, ProductData, OptionValue } from '~/types/Order';
import { apiFetch } from '~/utils/apiFetch';

export interface EnrichedOrderItem extends OrderItem {
  variant: ProductVariantDetails;
}

interface FetchOptions {
  request?: Request;
}

export async function enrichOrderItem(item: OrderItem, apiUrl: string, options?: FetchOptions): Promise<OrderItem> {
  if (typeof item.variant !== 'string') {
    return item;
  }

  try {
    const fetchOpts = options?.request ? { request: options.request } : undefined;

    // Fetch variant
    const variantRes = await apiFetch(`${apiUrl}${item.variant}`, {}, fetchOpts);
    if (!variantRes.ok) throw new Error('Variant fetch failed');
    const rawVariant = await variantRes.json();

    // Fetch option values with their options
    const optionValues = await Promise.all(
      (rawVariant.optionValues ?? []).map(async (ov: string | OptionValue) => {
        if (typeof ov !== 'string') return ov;

        try {
          const valueRes = await apiFetch(`${apiUrl}${ov}`, {}, fetchOpts);
          if (!valueRes.ok) return null;
          const val = await valueRes.json();

          if (!val.option || typeof val.option !== 'string') return val;

          const optionRes = await apiFetch(`${apiUrl}${val.option}`, {}, fetchOpts);
          if (!optionRes.ok) return val;
          const option = await optionRes.json();

          return { ...val, option };
        } catch (error) {
          console.error('Failed to fetch option value:', error);
          return null;
        }
      })
    );

    // Fetch product
    const productRes = await apiFetch(
      `${apiUrl}${rawVariant.product}?imageFilter=sylius_shop_product_small_thumbnail`,
      {},
      fetchOpts
    );
    if (!productRes.ok) throw new Error('Product fetch failed');
    const product: ProductData = await productRes.json();

    return {
      ...item,
      variant: {
        ...rawVariant,
        optionValues: optionValues.filter(Boolean),
        product,
      },
    };
  } catch (error) {
    console.error('Failed to enrich item:', item.id, error);
    return item;
  }
}

export async function enrichOrderItems(
  items: OrderItem[],
  apiUrl: string,
  options?: FetchOptions
): Promise<OrderItem[]> {
  return Promise.all(items.map((item) => enrichOrderItem(item, apiUrl, options)));
}
